import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import type { ExceptionPage, ExceptionRow, Filters, PartyCheckPage, PartyCheckRow } from "../lib/api.ts";
import { money, num } from "../lib/format.ts";
import { DrawerActions } from "./exportButton.tsx";
import { exportToExcel, fetchAllPages, moneyValue, type ExportColumn } from "../lib/tableExport.ts";
import { currentLang, plural, t, type L } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";
import { Th, useSortable, type SortColumn } from "./sortable.tsx";

/**
 * Две панели под плитками праздников (миграция 090).
 *
 * ЧЕКИ ПРАЗДНИКОВ — под плиткой «Выручка с банкетов (Syrve)». До 04.10.2026 под
 * ней не открывалось ничего: в строках кассы нет номера чека. Теперь плитка
 * сложена из чеков, и каждый чек можно открыть: номер заказа, гость, бронь и
 * сделка, к которой он относится.
 *
 * ИСКЛЮЧЕНИЯ — под плитками завершённых праздников. Каждое — запись с причиной:
 * почему этот праздник посчитан не так, как велит CRM. Без списка число «219»
 * рядом с «236 выигранных» было бы загадкой, а исправленная на экране дата —
 * подменой данных без объяснения.
 */

/** Проза этих панелей. Один блок в начале файла — см. App.tsx. */
const TX = {
  checksCounted: {
    one: { ru: "чек праздника", en: "party check" },
    few: { ru: "чека праздников", en: "party checks" },
    many: { ru: "чеков праздников", en: "party checks" },
  },
  exceptionsCounted: {
    one: { ru: "исключение", en: "exception" },
    few: { ru: "исключения", en: "exceptions" },
    many: { ru: "исключений", en: "exceptions" },
  },
  noChecks: { ru: "За этот период чеков праздников нет.", en: "No party checks in this period." },
  noExceptions: { ru: "За этот период исключений нет.", en: "No exceptions in this period." },
  colDay: { ru: "День", en: "Day" },
  colCheck: { ru: "Чек", en: "Check" },
  colGuest: { ru: "Гость", en: "Guest" },
  colAmount: { ru: "Сумма", en: "Amount" },
  colDeal: { ru: "Сделка", en: "Deal" },
  colWhat: { ru: "Что", en: "What" },
  colEffect: { ru: "Как учтено", en: "How it is counted" },
  colWhy: { ru: "Почему", en: "Why" },
  colStatus: { ru: "Состояние", en: "State" },
  banquetOrder: { ru: "банкетный заказ", en: "banquet order" },
  packageOrder: { ru: "обычный заказ с пакетом", en: "regular order with a package" },
  linkBooking: { ru: "по номеру брони", en: "by booking number" },
  /* Номер брони в чек не попал (резерв без предзаказа), а итог брони совпал с чеком. */
  linkBookingTotal: { ru: "по итогу брони", en: "by booking total" },
  linkManual: { ru: "вручную при сверке", en: "by hand in reconciliation" },
  notLinked: { ru: "не связан с бронью", en: "not linked to a booking" },
  dealDate: { ru: "праздник в сделке", en: "party date in the deal" },
  totalLine: { ru: "на сумму", en: "totalling" },
  showAffecting: { ru: "Изменили праздники", en: "Changed the parties" },
  showAll: { ru: "Все записи периода", en: "All records for the period" },
  /* Подсказка плитки описывает действующие; у полного списка она своя. */
  allHint: {
    ru: "все записи периода, включая ожидающие, исправленные в CRM и пояснения к чекам",
    en: "every record for the period, including those on standby, fixed in the CRM and notes on checks",
  },
  related: { ru: "основная сделка", en: "main deal" },
  sourceAudit: { ru: "сверка", en: "reconciliation" },
  sourceDetector: { ru: "найдено автоматически", en: "found automatically" },
  sourceManual: { ru: "вручную", en: "by hand" },
  checkWord: { ru: "чек", en: "check" },
  /* --- колонки выгрузки в Excel: вторая строка ячейки — отдельной колонкой */
  exOrderKind: { ru: "Вид заказа", en: "Order type" },
  exBooking: { ru: "Бронь", en: "Booking" },
  exAmountNative: { ru: "Сумма в валюте парка", en: "Amount in park currency" },
  exCurrency: { ru: "Валюта", en: "Currency" },
  exDealId: { ru: "ID сделки", en: "Deal ID" },
  exLink: { ru: "Как связан", en: "How linked" },
  exStage: { ru: "Стадия сделки", en: "Deal stage" },
  exNotes: { ru: "Пометки", en: "Notes" },
  exDealLink: { ru: "Ссылка на сделку", en: "Deal link" },
  exSource: { ru: "Источник", en: "Source" },
  exCheck: { ru: "Чек", en: "Check" },
  exCheckDay: { ru: "День чека", en: "Check day" },
  exCheckSum: { ru: "Сумма чека", en: "Check amount" },
  exRelated: { ru: "Основная сделка", en: "Main deal" },
  exDealDate: { ru: "Праздник в сделке", en: "Party date in the deal" },
  firstPage: { ru: "В начало", en: "First" },
  previousPage: { ru: "Назад", en: "Previous" },
  nextPage: { ru: "Далее", en: "Next" },
} as const;

/** Что исключение делает с праздником — коротко, для колонки «Как учтено». */
const EFFECT: Record<ExceptionRow["effect"], L> = {
  exclude: { ru: "не считается праздником", en: "not counted as a party" },
  redate: { ru: "считается в день чека", en: "counted on the check day" },
  include: { ru: "считается праздником по чеку", en: "counted as a party by the check" },
  link: { ru: "чек отнесён к сделке", en: "check assigned to the deal" },
  note: { ru: "пояснение, цифры не меняет", en: "explanation, no change to figures" },
};

const STATUS: Record<ExceptionRow["status"], L> = {
  active: { ru: "действует", en: "applies" },
  standby: { ru: "ждёт: сработает, если сделку выиграют", en: "on standby: applies if the deal is won" },
  resolved: { ru: "исправлено в CRM", en: "fixed in the CRM" },
  changed: { ru: "CRM изменили иначе — проверить", en: "CRM changed otherwise — review" },
  missing: { ru: "нет в данных", en: "not in the data" },
  retired: { ru: "снято", en: "withdrawn" },
};

/** Пояснения к чеку из хранилища исключений. */
const CHECK_NOTE: Record<string, L> = {
  check_without_deal: { ru: "праздник без сделки", en: "party without a deal" },
  check_owner: { ru: "привязан вручную", en: "matched by hand" },
};

export type PartyCheckDrill = {
  title: string;
  hint?: string;
  slice: { parkKey?: string };
  filters?: Partial<Filters>;
};

export type ExceptionDrill = {
  title: string;
  hint?: string;
  status: "affecting" | "all";
  filters?: Partial<Filters>;
};

const PAGE = 200;

/** Escape закрывает — как у остальных панелей. */
function useEscape(open: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

function DrawerHead({
  title,
  hint,
  onClose,
  exportRun,
  exportDisabled,
}: {
  title: string;
  hint: string;
  onClose: () => void;
  exportRun?: (progress: (done: number, total: number) => void) => Promise<void>; // i18n-ok: тип TypeScript, а не текст на экране
  exportDisabled?: boolean;
}) {
  return (
    <div className="drawer-head">
      <div>
        <div className="drawer-title">{title}</div>
        <div className="drawer-hint">{hint}</div>
      </div>
      <DrawerActions onClose={onClose} exportRun={exportRun} exportDisabled={exportDisabled} />
    </div>
  );
}

/* Сокращения месяцев — свои, а не из `toLocaleDateString`: браузер пишет
   «Sept», а примечания из базы — «Sep», и в одной строке стояли бы оба. */
const MONTH_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]; // i18n-ok: английские сокращения месяцев, русская ветка ниже цифрами

/** Дата `YYYY-MM-DD` → `ДД.ММ` по-русски, `D Mon` по-английски: в строке таблицы год лишний. */
function day(iso: string | null): string {
  if (iso === null) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return currentLang() === "ru"
    ? `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}`
    : `${d} ${MONTH_EN[m - 1] ?? ""}`;
}

/** Колонки выгрузки чеков праздников — те же, что у таблицы; вторая строка ячейки — рядом. */
function checkExportColumns(): Array<ExportColumn<PartyCheckRow>> {
  const linkLabel = (r: PartyCheckRow): string | null =>
    r.deal_id === null
      ? t(TX.notLinked)
      : t(r.deal_link === "manual" ? TX.linkManual : r.deal_link === "booking_total" ? TX.linkBookingTotal : TX.linkBooking);
  return [
    { title: t(TX.colDay), kind: "date", width: 12, value: (r) => r.business_day },
    { title: t(UI.park), width: 24, value: (r) => r.park_name },
    { title: t(TX.colCheck), kind: "number", width: 10, value: (r) => (r.order_num === null ? null : Number(r.order_num)) },
    { title: t(TX.exOrderKind), width: 22, value: (r) => t(r.is_banquet ? TX.banquetOrder : TX.packageOrder) },
    { title: t(TX.colGuest), width: 26, value: (r) => r.guest_name },
    { title: t(TX.exBooking), width: 12, value: (r) => r.booking_ref },
    { title: t(TX.colAmount), kind: "money", width: 12, value: (r) => moneyValue(r.revenue_aed_minor) },
    {
      title: t(TX.exAmountNative),
      kind: "money",
      width: 14,
      value: (r) => (r.currency !== "AED" ? moneyValue(r.revenue_minor, r.currency) : null),
    },
    { title: t(TX.exCurrency), width: 8, value: (r) => r.currency },
    {
      title: t(TX.colDeal),
      width: 34,
      value: (r) => (r.deal_id === null ? null : r.deal_title === null || r.deal_title === "" ? `#${r.deal_id}` : r.deal_title),
    },
    { title: t(TX.exDealId), kind: "number", width: 10, value: (r) => (r.deal_id === null ? null : Number(r.deal_id)) },
    { title: t(TX.exLink), width: 22, value: linkLabel },
    { title: t(TX.exStage), width: 18, value: (r) => r.deal_stage_name },
    { title: t(TX.exDealDate), kind: "date", width: 14, value: (r) => r.deal_event_date },
    {
      title: t(TX.exNotes),
      width: 22,
      value: (r) => r.exception_kinds.filter((k) => CHECK_NOTE[k] !== undefined).map((k) => t(CHECK_NOTE[k] as L)).join("; "),
    },
    { title: t(TX.exDealLink), width: 50, value: (r) => r.deal_url },
  ];
}

const CHECK_COLS: Record<"day" | "park" | "check" | "guest" | "amount" | "deal", SortColumn<PartyCheckRow>> = {
  day: { kind: "text", get: (r) => r.business_day },
  park: { kind: "text", get: (r) => r.park_name },
  check: { kind: "number", get: (r) => r.order_num },
  guest: { kind: "text", get: (r) => r.guest_name },
  amount: { kind: "number", get: (r) => r.revenue_aed_minor },
  deal: { kind: "text", get: (r) => r.deal_title },
};

export function PartyCheckDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: PartyCheckDrill | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<PartyCheckPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const key = drill === null ? "" : JSON.stringify([drill.slice, filters]);

  useEffect(() => {
    setOffset(0);
  }, [key]);

  useEffect(() => {
    if (drill === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .partyChecks(filters, drill.slice, PAGE, offset)
      .then((p) => {
        if (alive) setPage(p);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset]);

  useEscape(drill !== null, onClose);

  const rows = page?.rows ?? [];
  const total = page?.total ?? 0;
  /* Сортировка — только когда весь срез на одной странице, как у сделок. */
  const S = useSortable(rows, CHECK_COLS, { partial: total > rows.length });

  if (drill === null) return null;

  /* Весь список под цифрой: целиком на экране — в отсортированном порядке, иначе со всех страниц. */
  const runExport = async (progress: (done: number, total: number) => void): Promise<void> => {
    const all =
      total <= rows.length
        ? S.rows
        : await fetchAllPages((limit, off) => api.partyChecks(filters, drill.slice, limit, off), progress);
    await exportToExcel(drill.title, checkExportColumns(), all);
  };

  const hint =
    loading && page === null
      ? t(UI.loading)
      : `${num(total)} ${plural(total, TX.checksCounted)} ${t(TX.totalLine)} ${money(page?.sum_aed_minor ?? 0, "AED")}` +
        (drill.hint ? ` · ${drill.hint}` : "");

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <DrawerHead
          title={drill.title}
          hint={hint}
          onClose={onClose}
          exportRun={runExport}
          exportDisabled={loading || total === 0}
        />
        <div className="drawer-body">
          {error !== null && <div className="error">{error}</div>}
          {error === null && !loading && rows.length === 0 && <div className="empty">{t(TX.noChecks)}</div>}
          {rows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <Th sort={S} col="day">{t(TX.colDay)}</Th>
                    <Th sort={S} col="park">{t(UI.park)}</Th>
                    <Th sort={S} col="check" num>{t(TX.colCheck)}</Th>
                    <Th sort={S} col="guest">{t(TX.colGuest)}</Th>
                    <Th sort={S} col="amount" num>{t(TX.colAmount)}</Th>
                    <Th sort={S} col="deal">{t(TX.colDeal)}</Th>
                  </tr>
                </thead>
                <tbody>
                  {S.rows.map((r) => (
                    <tr key={r.order_id}>
                      <td className="nowrap">{day(r.business_day)}</td>
                      <td>{r.park_name ?? "—"}</td>
                      <td className="num">
                        {r.order_num ?? "—"}
                        <div className="sub">{t(r.is_banquet ? TX.banquetOrder : TX.packageOrder)}</div>
                      </td>
                      <td>
                        {r.guest_name ?? "—"}
                        {r.booking_ref !== null && <div className="sub">{r.booking_ref}</div>}
                      </td>
                      <td className="num">
                        {money(r.revenue_aed_minor, "AED", { withCode: false })}
                        {r.currency !== "AED" && (
                          <div className="sub">{money(r.revenue_minor, r.currency, { withCode: true })}</div>
                        )}
                      </td>
                      <td>
                        {r.deal_id === null || r.deal_url === null ? (
                          <span className="sub">{t(TX.notLinked)}</span>
                        ) : (
                          <>
                            <a href={r.deal_url} target="_blank" rel="noreferrer">
                              {r.deal_title === null || r.deal_title === "" ? `#${r.deal_id}` : r.deal_title}
                            </a>
                            <div className="sub">
                              {t(
                                r.deal_link === "manual"
                                  ? TX.linkManual
                                  : r.deal_link === "booking_total"
                                    ? TX.linkBookingTotal
                                    : TX.linkBooking,
                              )}
                              {r.deal_stage_name !== null ? ` · ${r.deal_stage_name}` : ""}
                              {r.deal_event_date !== null && r.deal_event_date !== r.business_day
                                ? ` · ${t(TX.dealDate)} ${day(r.deal_event_date)}`
                                : ""}
                            </div>
                          </>
                        )}
                        {r.exception_kinds
                          .filter((k) => CHECK_NOTE[k] !== undefined)
                          .map((k) => (
                            <span key={k} className="tag tag-warn" style={{ marginRight: 4 }}>
                              {t(CHECK_NOTE[k] as L)}
                            </span>
                          ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > rows.length && (
            <div className="drawer-foot">
              <span style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn-ghost" disabled={offset === 0 || loading} onClick={() => setOffset(0)}>
                  {t(TX.firstPage)}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  {t(TX.previousPage)}
                </button>
              </span>
              <span className="sub">
                {num(offset + 1)}–{num(offset + rows.length)} {t(UI.of)} {num(total)}
              </span>
              <button
                type="button"
                className="btn-ghost"
                disabled={offset + rows.length >= total || loading}
                onClick={() => setOffset(offset + PAGE)}
              >
                {t(TX.nextPage)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const SOURCE: Record<ExceptionRow["source"], L> = {
  audit: TX.sourceAudit,
  detector: TX.sourceDetector,
  manual: TX.sourceManual,
};

/** Что именно поменялось — одной строкой из полей записи, а не из текста примечания. */
function effectDetail(r: ExceptionRow): string {
  const parts: string[] = [t(EFFECT[r.effect])];
  if (r.effect === "redate" && r.corrected_date !== null) {
    parts[0] = `${t(EFFECT.redate)}: ${day(r.seen_date)} → ${day(r.corrected_date)}`;
  }
  if (r.effect === "include" && r.corrected_date !== null) {
    parts.push(day(r.corrected_date));
  }
  if (r.amount_minor !== null && r.currency !== null) {
    parts.push(money(r.amount_minor, r.currency, { withCode: true }));
  }
  return parts.join(" · ");
}

/** Колонки выгрузки исключений — те же, что у таблицы; вторая строка ячейки — рядом. */
function exceptionExportColumns(): Array<ExportColumn<ExceptionRow>> {
  const ru = currentLang() === "ru";
  return [
    { title: t(TX.colWhat), width: 30, value: (r) => (ru ? r.label_ru : r.label_en) },
    { title: t(TX.exSource), width: 20, value: (r) => t(SOURCE[r.source]) },
    { title: t(UI.park), width: 24, value: (r) => r.park_name },
    {
      title: t(TX.colDeal),
      width: 34,
      value: (r) =>
        r.entity !== "deal" ? null : r.deal_title === null || r.deal_title === "" ? `#${r.deal_id}` : r.deal_title,
    },
    { title: t(TX.exDealId), kind: "number", width: 10, value: (r) => (r.deal_id === null ? null : Number(r.deal_id)) },
    { title: t(TX.exStage), width: 18, value: (r) => r.deal_stage_name },
    { title: t(TX.exDealDate), kind: "date", width: 14, value: (r) => r.deal_event_date },
    { title: t(TX.exCheck), kind: "number", width: 10, value: (r) => (r.order_num === null ? null : Number(r.order_num)) },
    { title: t(TX.exCheckDay), kind: "date", width: 12, value: (r) => r.order_day },
    {
      title: t(TX.exCheckSum),
      kind: "money",
      width: 12,
      value: (r) => (r.order_revenue_minor === null || r.order_currency === null ? null : moneyValue(r.order_revenue_minor, r.order_currency)),
    },
    { title: t(TX.exRelated), kind: "number", width: 12, value: (r) => (r.related_deal_id === null ? null : Number(r.related_deal_id)) },
    { title: t(TX.colEffect), width: 34, value: (r) => effectDetail(r) },
    { title: t(TX.colWhy), width: 60, value: (r) => (ru ? r.note_ru : r.note_en) },
    { title: t(TX.colStatus), width: 26, value: (r) => t(STATUS[r.status]) },
    { title: t(TX.exDealLink), width: 50, value: (r) => r.deal_url },
  ];
}

export function ExceptionDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: ExceptionDrill | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<ExceptionPage | null>(null);
  /** Что показываем: только изменившие праздники или все записи периода. */
  const [status, setStatus] = useState<"affecting" | "all">("affecting");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const key = drill === null ? "" : JSON.stringify([filters, status]);

  /* Новая плитка — режим с плитки, а не тот, что остался от прошлой панели. */
  useEffect(() => {
    if (drill !== null) setStatus(drill.status);
  }, [drill]);

  useEffect(() => {
    if (drill === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .exceptions(filters, status)
      .then((p) => {
        if (alive) setPage(p);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEscape(drill !== null, onClose);

  if (drill === null) return null;
  const rows = page?.rows ?? [];
  const total = page?.total ?? 0;
  const ru = currentLang() === "ru";
  const hint =
    (loading && page === null ? t(UI.loading) : `${num(total)} ${plural(total, TX.exceptionsCounted)}`) +
    (status === "all" ? ` · ${t(TX.allHint)}` : drill.hint ? ` · ${drill.hint}` : "");

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <DrawerHead
          title={drill.title}
          hint={hint}
          onClose={onClose}
          /* Исключения приходят списком целиком — выгружается то, что на экране,
             в выбранном режиме: «изменили праздники» или «все записи периода». */
          exportRun={async () => exportToExcel(drill.title, exceptionExportColumns(), rows)}
          exportDisabled={loading || rows.length === 0}
        />
        <div className="drawer-body">
          <div className="seg" role="group" style={{ marginBottom: 12 }}>
            <button type="button" aria-pressed={status === "affecting"} onClick={() => setStatus("affecting")}>
              {t(TX.showAffecting)}
            </button>
            <button type="button" aria-pressed={status === "all"} onClick={() => setStatus("all")}>
              {t(TX.showAll)}
            </button>
          </div>
          {error !== null && <div className="error">{error}</div>}
          {error === null && !loading && rows.length === 0 && <div className="empty">{t(TX.noExceptions)}</div>}
          {rows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t(TX.colWhat)}</th>
                    <th>{t(UI.park)}</th>
                    <th>{t(TX.colDeal)}</th>
                    <th>{t(TX.colEffect)}</th>
                    <th>{t(TX.colWhy)}</th>
                    <th>{t(TX.colStatus)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.exception_id}>
                      <td>
                        <b>{ru ? r.label_ru : r.label_en}</b>
                        <div className="sub">{t(SOURCE[r.source])}</div>
                      </td>
                      <td>{r.park_name ?? "—"}</td>
                      <td>
                        {r.entity === "deal" && r.deal_url !== null ? (
                          <>
                            <a href={r.deal_url} target="_blank" rel="noreferrer">
                              {r.deal_title === null || r.deal_title === "" ? `#${r.deal_id}` : r.deal_title}
                            </a>
                            <div className="sub">
                              #{r.deal_id}
                              {r.deal_stage_name !== null ? ` · ${r.deal_stage_name}` : ""}
                              {r.deal_event_date !== null ? ` · ${day(r.deal_event_date)}` : ""}
                            </div>
                          </>
                        ) : (
                          <>
                            {t(TX.checkWord)} {r.order_num ?? "—"}
                            <div className="sub">
                              {day(r.order_day ?? r.event_date)}
                              {r.order_revenue_minor !== null && r.order_currency !== null
                                ? ` · ${money(r.order_revenue_minor, r.order_currency, { withCode: true })}`
                                : ""}
                            </div>
                          </>
                        )}
                        {r.related_deal_url !== null && (
                          <div className="sub">
                            {r.entity === "deal" ? t(TX.related) : t(TX.colDeal).toLowerCase()}:{" "}
                            <a href={r.related_deal_url} target="_blank" rel="noreferrer">
                              #{r.related_deal_id}
                            </a>
                          </div>
                        )}
                      </td>
                      <td>{effectDetail(r)}</td>
                      <td style={{ minWidth: 260 }}>{ru ? r.note_ru : r.note_en}</td>
                      <td>
                        <span className={r.status === "active" ? "tag tag-warn" : "tag"}>{t(STATUS[r.status])}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
